import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { FaEye, FaEyeSlash, FaLock, FaCheck, FaTimes } from "react-icons/fa";
import { validatePassword } from "../utils/validation";
import { prepareSignInRedirect } from "../utility/routeUtils";

export default function ResetPasswordForm() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });
  const [passwordMatch, setPasswordMatch] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkToken = async () => {
      const params = new URLSearchParams(location.search);
      const confirmationUrl = params.get("confirmation_url");

      if (!confirmationUrl) {
        setIsValidToken(false);
        setError(
          "Invalid or expired reset link. Please request a new password reset."
        );
        return;
      }

      try {
        // Parse the confirmation URL to get the token
        const url = new URL(confirmationUrl);
        const token = url.searchParams.get("token");
        const type = url.searchParams.get("type");

        if (!token || type !== "recovery") {
          setIsValidToken(false);
          setError("Invalid reset link. Please request a new password reset.");
          return;
        }

        // Verify the token immediately
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: "recovery",
        });

        if (verifyError) {
          console.error("Token verification error:", verifyError);
          setIsValidToken(false);
          // Sanitize error message for user display
          if (verifyError.message.includes("already been used")) {
            setError(
              "This password reset link has already been used. Please request a new password reset."
            );
          } else if (verifyError.message.includes("expired")) {
            setError(
              "This password reset link has expired. Please request a new password reset."
            );
          } else {
            setError(
              "Invalid reset link. Please request a new password reset."
            );
          }
          return;
        }

        // Store the token for later use
        sessionStorage.setItem("resetToken", token);
        setIsValidToken(true);
      } catch (err) {
        console.error("Error parsing confirmation URL:", err);
        setIsValidToken(false);
        setError(
          "Invalid reset link format. Please request a new password reset."
        );
      }
    };

    checkToken();
  }, [location]);

  // Update password checks when password changes
  useEffect(() => {
    if (password) {
      const validation = validatePassword(password);
      setPasswordChecks(validation.checks);
    }
  }, [password]);

  // Check password match when either password changes
  useEffect(() => {
    if (confirmPassword) {
      setPasswordMatch(password === confirmPassword);
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!passwordMatch) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      setLoading(false);
      return;
    }

    try {
      const token = sessionStorage.getItem("resetToken");

      if (!token) {
        setError("Reset session expired. Please request a new password reset.");
        setLoading(false);
        return;
      }

      // Update the password using the token
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error("Password update error:", updateError);
        // Sanitize error message for user display
        if (updateError.message.includes("already been used")) {
          setError(
            "This password reset link has already been used. Please request a new password reset."
          );
        } else if (updateError.message.includes("expired")) {
          setError(
            "This password reset link has expired. Please request a new password reset."
          );
        } else if (
          updateError.message.includes("different from the old password")
        ) {
          setError(
            "Your new password must be different from your current password."
          );
        } else {
          setError(
            "Failed to reset password. Please try again or request a new password reset."
          );
        }
      } else {
        // Clear the token
        sessionStorage.removeItem("resetToken");
        setSuccess(true);
        setTimeout(() => {
          // Store current location for redirect after sign-in
          prepareSignInRedirect(window.location.pathname);
          navigate("/signin", {
            state: {
              message:
                "Password has been reset successfully. Please sign in with your new password.",
            },
          });
        }, 2000);
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isValidToken) {
    return (
      <section className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8 card-board p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-lg bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="mt-6 heading-display text-3xl">
              Invalid Reset Link
            </h2>
            <p className="mt-2 text-sm text-brand-muted">{error}</p>
            <div className="mt-6">
              <a
                href="/forgot-password"
                className="text-brand-gold-ink hover:text-brand-charcoal font-medium"
              >
                Request a new password reset
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (success) {
    return (
      <section className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8 card-board p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-lg bg-brand-charcoal">
              <svg
                className="h-6 w-6 text-brand-cream"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mt-6 heading-display text-3xl">
              Password Reset Successful
            </h2>
            <p className="mt-2 text-sm text-brand-muted">
              Your password has been reset successfully. Redirecting to sign
              in...
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 card-board p-8">
        <div>
          <h2 className="mt-6 text-center heading-display text-3xl">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-brand-muted">
            Please enter your new password below.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink focus:z-10 sm:text-sm"
                placeholder="New password"
              />
              <button
                type="button"
                tabIndex="-1"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                {showPassword ? (
                  <FaEyeSlash className="h-5 w-5" />
                ) : (
                  <FaEye className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`appearance-none rounded-md relative block w-full px-3 py-3 pl-10 border ${
                  !passwordMatch && confirmPassword
                    ? "border-red-300"
                    : "border-gray-300"
                } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink focus:z-10 sm:text-sm`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                tabIndex="-1"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                {showConfirmPassword ? (
                  <FaEyeSlash className="h-5 w-5" />
                ) : (
                  <FaEye className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className="flex items-center text-sm">
                {passwordMatch ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={passwordMatch ? "text-brand-charcoal" : "text-red-600"}
                >
                  Passwords match
                </span>
              </div>
            )}

            {/* Password Requirements */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm">
                {passwordChecks.length ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={
                    passwordChecks.length ? "text-brand-charcoal" : "text-gray-600"
                  }
                >
                  At least 8 characters
                </span>
              </div>
              <div className="flex items-center text-sm">
                {passwordChecks.uppercase ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={
                    passwordChecks.uppercase
                      ? "text-brand-charcoal"
                      : "text-gray-600"
                  }
                >
                  At least one uppercase letter
                </span>
              </div>
              <div className="flex items-center text-sm">
                {passwordChecks.lowercase ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={
                    passwordChecks.lowercase
                      ? "text-brand-charcoal"
                      : "text-gray-600"
                  }
                >
                  At least one lowercase letter
                </span>
              </div>
              <div className="flex items-center text-sm">
                {passwordChecks.number ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={
                    passwordChecks.number ? "text-brand-charcoal" : "text-gray-600"
                  }
                >
                  At least one number
                </span>
              </div>
              <div className="flex items-center text-sm">
                {passwordChecks.special ? (
                  <FaCheck className="text-brand-charcoal mr-2" />
                ) : (
                  <FaTimes className="text-red-500 mr-2" />
                )}
                <span
                  className={
                    passwordChecks.special ? "text-brand-charcoal" : "text-gray-600"
                  }
                >
                  At least one special character
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-3 px-4 rounded-lg text-sm font-display font-bold tracking-wide ${
                loading
                  ? "bg-brand-mist text-brand-charcoal cursor-not-allowed"
                  : "bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold"
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-charcoal"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Resetting...
                </div>
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
